import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthService, VehicleRepairService, VehicleService } from "../../services";
import { Upload, Trash } from "react-bootstrap-icons";
import { Breadcrumb } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment";
import {
  REPAIR_PART_NAME,
  REPAIR_PART_NAME_TEXT,
  QUANTITY_OPTIONS,
  truncateReceiptFileLabel,
} from "../../shared";

const AddRepairRecord = () => {
  const navigate = useNavigate();
  const params = useParams();

  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [existingRepairs, setExistingRepairs] = useState([]);
  const [repairReceiptsById, setRepairReceiptsById] = useState({});

  const [partName, setPartName] = useState('');
  const [replacementDate, setReplacementDate] = useState(null);
  const [mileage, setMileage] = useState('');
  const [quantity, setQuantity] = useState('');
  const [cost, setCost] = useState('');
  const [partsCharges, setPartsCharges] = useState('');
  const [laborCharges, setLaborCharges] = useState('');
  const [tax, setTax] = useState('');
  const [location, setLocation] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [nextReminder, setNextReminder] = useState('');

  const normalizeCurrencyInput = (value = '') =>
    String(value).replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

  const formatCurrencyDisplay = (value = '') => {
    const normalized = normalizeCurrencyInput(value);
    if (!normalized) return '';
    const amount = Number(normalized);
    if (!Number.isFinite(amount)) return '';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const normalizeMileageInput = (value = '') => String(value).replace(/[^0-9]/g, '');

  const formatMileageDisplay = (value = '') => {
    const normalized = normalizeMileageInput(value);
    if (!normalized) return '';
    return Number(normalized).toLocaleString();
  };

  useEffect(() => {
    if (!AuthService.canEditVehicleRepairs()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page.');
      AuthService.logout();
      navigate('/login');
    }
    VehicleService.getVehicle(params.id).then((data) => {
      setCurrentVehicle(data.data);
    });
  }, []);

  useEffect(() => {
    if (currentVehicle) {
      fetchRepairs();
    }
  }, [currentVehicle]);

  const fetchRepairs = () => {
    const vid = currentVehicle.id;
    VehicleRepairService.getAll(vid)
      .then(res => setExistingRepairs(res.data || []))
      .catch(() => setExistingRepairs([]));
    VehicleService.getAllVechileFiles(vid, 'repair', 'repair')
      .then((res) => {
        const list = res?.data?.data?.files || [];
        setRepairReceiptsById(VehicleService.indexRepairReceiptFiles(list));
      })
      .catch(() => setRepairReceiptsById({}));
  };

  const formatDateForBackend = (date) => {
    if (!date) return '';
    return moment(date).format('MM/DD/YYYY');
  };

  const handleSave = () => {
    if (!partName || !partName.trim()) {
      window.alert('Please enter a part name.');
      return;
    }
    if (!replacementDate) {
      window.alert('Please select a replacement date.');
      return;
    }
    if (!cost || !normalizeCurrencyInput(cost)) {
      window.alert('Please enter total cost.');
      return;
    }
    if (!location || !location.trim()) {
      window.alert('Please enter a location.');
      return;
    }
    const data = {
      vehicle: currentVehicle?.id,
      part_name: partName.trim(),
      part_name_other: '',
      repair_date: formatDateForBackend(replacementDate),
      mileage_at_replacement: formatMileageDisplay(mileage),
      quantity,
      repair_price: formatCurrencyDisplay(cost),
      parts_charges: formatCurrencyDisplay(partsCharges),
      labor_charges: formatCurrencyDisplay(laborCharges),
      tax: formatCurrencyDisplay(tax),
      repair_location: location.trim(),
      next_replacement_reminder: formatMileageDisplay(nextReminder)
    };
    VehicleRepairService.createNewVehicleRepair(data)
      .then((result) => {
        const record = result.data;
        const repairId =
          record && (record.id != null
            ? record.id
            : record._id != null
              ? String(record._id)
              : null);
        const vehicleId = currentVehicle?.id ?? params.id;
        const doReset = () => {
          setPartName('');
          setReplacementDate(null);
          setMileage('');
          setQuantity('');
          setCost('');
          setPartsCharges('');
          setLaborCharges('');
          setTax('');
          setLocation('');
          setReceiptFile(null);
          setNextReminder('');
          fetchRepairs();
        };
        if (!receiptFile) {
          doReset();
          return;
        }
        if (repairId == null || vehicleId == null) {
          window.alert(
            'Repair was saved, but the receipt was not uploaded (missing record or vehicle id).'
          );
          doReset();
          return;
        }
        const fd = new FormData();
        fd.append('file', receiptFile);
        return VehicleService.uploadVechileFile(
          fd,
          vehicleId,
          repairId,
          'repair',
          replacementDate || new Date()
        )
          .then(() => {
            doReset();
          })
          .catch((uploadErr) => {
            console.error('Receipt upload failed:', uploadErr);
            const msg =
              uploadErr?.response?.data?.message || uploadErr?.message || 'Upload failed';
            window.alert('Repair was saved, but the receipt could not be uploaded: ' + msg);
            doReset();
          });
      })
      .catch((error) => {
        console.error('Save repair record failed:', error);
        let message = 'Could not save repair record.';
        const d = error?.response?.data;
        if (d?.message) message = d.message;
        window.alert(message);
      });
  };

  const deleteRepair = (repair) => {
    if (window.confirm('Are you sure you want to delete this repair record?')) {
      const id = repair?.id ?? repair?._id;
      if (id == null) return;
      VehicleRepairService.deleteVehicleRepair(id).then(() => fetchRepairs());
    }
  };

  const goBack = () => {
    navigate(`/vehicles/${params.id}?tab=documents`);
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          <Breadcrumb.Item href="/vehicles/list">Vehicles Information</Breadcrumb.Item>
          <Breadcrumb.Item href={`/vehicles/${params.id}?tab=documents`}>View Vehicle Information</Breadcrumb.Item>
          <Breadcrumb.Item active>Add Repair Record</Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>Add Repair Record <button className="btn btn-link btn-sm" onClick={goBack}>Back</button></h4>
        </div>
      </div>
      <div className="app-main-content-list-container form-page">
        <div className="app-main-content-list-func-container">
          <h6 className="text-primary">Repair & Maintenance Record</h6>

          <div className="field-label mb-2">Add New Record</div>
          <div className="app-main-content-fields-section">
            <div className="me-4">
              <div className="field-label">Part Name<span className="required">*</span></div>
              <input
                type="text"
                placeholder="e.g., Brake pad"
                value={partName}
                onChange={e => setPartName(e.target.value)}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Replacement Date<span className="required">*</span></div>
              <DatePicker
                selected={replacementDate}
                onChange={(date) => setReplacementDate(date)}
                dateFormat="MM/dd/yyyy"
                placeholderText="e.g., 03/01/2024"
                className="form-control"
              />
            </div>
            <div className="me-4">
              <div className="field-label">Mileage at Replacement</div>
              <input
                type="text"
                placeholder="e.g., 48,000"
                value={mileage}
                onFocus={(e) => setMileage(normalizeMileageInput(e.target.value))}
                onChange={(e) => setMileage(normalizeMileageInput(e.target.value))}
                onBlur={(e) => setMileage(formatMileageDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Quantity</div>
              <select value={quantity} onChange={e => setQuantity(e.target.value)}>
                <option value="">Select...</option>
                {QUANTITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="app-main-content-fields-section">
            <div className="me-4">
              <div className="field-label">Total Cost<span className="required">*</span></div>
              <input
                type="text"
                placeholder="e.g., $250.00"
                value={cost}
                onFocus={(e) => setCost(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setCost(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setCost(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Parts Charges</div>
              <input
                type="text"
                placeholder="e.g., $50.00"
                value={partsCharges}
                onFocus={(e) => setPartsCharges(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setPartsCharges(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setPartsCharges(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Labor Charges</div>
              <input
                type="text"
                placeholder="e.g., $50.00"
                value={laborCharges}
                onFocus={(e) => setLaborCharges(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setLaborCharges(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setLaborCharges(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Tax</div>
              <input
                type="text"
                placeholder="e.g., $50.00"
                value={tax}
                onFocus={(e) => setTax(normalizeCurrencyInput(e.target.value))}
                onChange={(e) => setTax(normalizeCurrencyInput(e.target.value))}
                onBlur={(e) => setTax(formatCurrencyDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Location<span className="required">*</span></div>
              <input type="text" placeholder="e.g., Rockville Auto Center" value={location} onChange={e => setLocation(e.target.value)} />
            </div>
            <div className="me-4">
              <div className="field-label">Next Replacement Reminder</div>
              <input
                type="text"
                placeholder="e.g., 78,000"
                value={nextReminder}
                onFocus={(e) => setNextReminder(normalizeMileageInput(e.target.value))}
                onChange={(e) => setNextReminder(normalizeMileageInput(e.target.value))}
                onBlur={(e) => setNextReminder(formatMileageDisplay(e.target.value))}
              />
            </div>
            <div className="me-4">
              <div className="field-label">Receipt Upload</div>
              <label className="custom-file-upload">
                <Upload width={20} color={"#fff"} className="me-2" /> Upload
                <input type="file" onChange={(e) => setReceiptFile(e.target.files[0])} />
              </label>
              <div className="file-name" title={receiptFile?.name}>
                {receiptFile?.name ? truncateReceiptFileLabel(receiptFile.name) : ""}
              </div>
            </div>
          </div>

          <div className="list row mb-4 mt-3">
            <div className="col-md-12 col-sm-12 col-xs-12">
              <button className="btn btn-default btn-sm float-right" onClick={goBack}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={handleSave}> Save </button>
            </div>
          </div>

          {existingRepairs.length > 0 && (
            <>
              <div className="field-label mb-2">Existing Records</div>
              <table className="table table-sm table-bordered mb-4">
                <thead>
                  <tr>
                    <th>Part Name</th>
                    <th>Date</th>
                    <th>Receipt</th>
                    <th>Mileage</th>
                    <th>Qty</th>
                    <th>Total Cost</th>
                    <th>Parts Charges</th>
                    <th>Labor Charges</th>
                    <th>Tax</th>
                    <th>Location</th>
                    <th>Next Reminder</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {existingRepairs.map((repair) => {
                    const rid = repair?.id != null ? String(repair.id) : repair?._id != null ? String(repair._id) : '';
                    const receiptFiles = rid && repairReceiptsById[rid] ? repairReceiptsById[rid] : [];
                    return (
                    <tr key={repair.id || repair._id}>
                      <td>
                        {repair.part_name === REPAIR_PART_NAME.OTHER
                          ? (repair.part_name_other || REPAIR_PART_NAME_TEXT[repair.part_name] || '-')
                          : (REPAIR_PART_NAME_TEXT[repair.part_name] || repair.repair_description || repair.part_name || '-')}
                      </td>
                      <td>{repair.repair_date || '-'}</td>
                      <td>
                        {receiptFiles.length === 0
                          ? '—'
                          : receiptFiles.map((f, i) => (
                            <a
                              key={`${f.name}-${i}`}
                              href={VehicleService.getPhysicalFileDownloadUrl(f.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="d-inline me-1"
                              title={f.name}
                            >
                              {truncateReceiptFileLabel(f.name)}
                            </a>
                          ))}
                      </td>
                      <td>{repair.mileage_at_replacement || '-'}</td>
                      <td>{repair.quantity || '-'}</td>
                      <td>{repair.repair_price || '-'}</td>
                      <td>{repair.parts_charges || '-'}</td>
                      <td>{repair.labor_charges || '-'}</td>
                      <td>{repair.tax || '-'}</td>
                      <td>{repair.repair_location || '-'}</td>
                      <td>{repair.next_replacement_reminder || '-'}</td>
                      <td>
                        <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => deleteRepair(repair)}>
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AddRepairRecord;
